import _ from 'lodash'
class CustomOverlay {
  constructor(marker, id, callback) {
    this.id = id;
    this.marker_ = marker;
    this.overlay_ = null;
    this.container_ = null;
    this.callback_ = callback;
    this.callbackFired_ = false;
    this.listeners = [];
    this.createOverlay();
  }

  createOverlay() {
    this.overlay_ = new google.maps.OverlayView(); // eslint-disable-line no-undef
    this.overlay_.onAdd = this.onAdd.bind(this);
    this.overlay_.draw = this.draw.bind(this);
    this.overlay_.onRemove = this.onRemove.bind(this);
    this.overlay_.onMove = this.onMove.bind(this);
  }

  cancelEvents() {
    const events = ['mousedown', 'mousemove', 'mouseover',
      'mouseout', 'mouseup', 'mousewheel',
      'DOMMouseScroll', 'touchstart', 'touchend', 'click',
      'touchmove', 'dblclick', 'contextmenu'];
    // Use native addEventListener in BUBBLE phase so child elements (buttons,
    // links inside the modal) receive events normally. Once the event bubbles
    // up to the container, stopPropagation prevents it from reaching the map
    // and triggering landmark/geofence shape click handlers underneath.
    for (let i = 0; i < events.length; i++) {
      const event = events[i];
      const handler = function (e) {
        e.cancelBubble = true;
        if (e.stopPropagation) {
          e.stopPropagation();
        }
      };
      this.container_.addEventListener(event, handler);
      this.listeners.push({ event, handler });
    }
  }

  /**
   * onAdd is called when the map's panes are ready and the overlay has been
   * added to the map.
   */
  onAdd() {
    this.container_ = document.createElement("div");
    this.container_.style.borderStyle = "none";
    this.container_.style.borderWidth = "0px";
    this.container_.style.position = "absolute";
    this.container_.style.top = '0px';
    this.container_.style.left = '0px';
    /* Pane reference (kept for documentation, not used as parent here):
          MapPanes.mapPane (Level 0)
          MapPanes.overlayLayer (Level 1)
          MapPanes.markerLayer (Level 2)
          MapPanes.overlayMouseTarget (Level 3)
          MapPanes.floatPane (Level 4)
     *
     * Stacking strategy on vector mapId basemaps
     * ──────────────────────────────────────────
     * The deck.gl WebGL marker canvas now lives INSIDE `.gm-style`
     * (see WebGLMarkerLayer._raiseCanvasAboveMarkers). Google appends
     * every pushed map control (`map.controls[POSITION].push(...)`)
     * AFTER the panes container inside `.gm-style` as well.
     *
     * If we attach this modal to `mapDiv` (sibling of `.gm-style`) with
     * an explicit z-index, it would always paint above `.gm-style`
     * (which uses `z-index: auto`) — including the map control buttons,
     * which is the exact bug the user reported.
     *
     * Fix: attach the modal INSIDE `.gm-style`, immediately after the
     * deck.gl marker container. All siblings of `.gm-style` use
     * `z-index: auto`, so DOM order alone decides stacking:
     *
     *   panes (basemap, landmarks)  →  trails  →  markers  →
     *   THIS MODAL  →  control containers (buttons stay on top)
     *
     * `fromLatLngToContainerPixel` returns coordinates relative to
     * `mapDiv`. Because `.gm-style` fills the exact same box as
     * `mapDiv`, positioning still works identically.
     */
    const map = this.overlay_.getMap?.();
    const mapDiv = map?.getDiv?.();
    const panes = this.overlay_.getPanes?.();
    const panesContainer = panes?.floatPane?.parentElement;
    const gmStyle = panesContainer?.parentElement;

    let attached = false;
    if (gmStyle && mapDiv && gmStyle.parentElement === mapDiv) {
      // Prefer to insert immediately after the deck.gl marker container so
      // we paint above unit markers but below any control container Google
      // appends later in `.gm-style`.
      const markerCanvas = gmStyle.querySelector('#deckgl-overlay[data-layer-type="markers"]');
      const markerContainer = markerCanvas?.parentElement;
      const anchor = (markerContainer && markerContainer.parentElement === gmStyle)
        ? markerContainer.nextSibling
        : panesContainer.nextSibling;
      gmStyle.insertBefore(this.container_, anchor);
      this._parentEl_ = gmStyle;
      attached = true;
    }
    if (!attached) {
      // Fallbacks: panes (legacy raster basemap) or mapDiv (last resort).
      // The explicit z-index only matters in these fallback paths.
      this.container_.style.zIndex = '200';
      if (panes?.floatPane) {
        this._parentEl_ = panes.floatPane;
        panes.floatPane.appendChild(this.container_);
      } else if (mapDiv) {
        this._parentEl_ = mapDiv;
        mapDiv.appendChild(this.container_);
      }
    }
    this.cancelEvents();
  }

  draw() {
    const overlayProjection = this.overlay_.getProjection();
    // Prefer the explicit position set by onMove() (called from
    // updateModalPositionTrails) over the marker instance position.
    // onMove stores a google.maps.LatLng on this.overlay_.position;
    // without this priority, the value passed to onMove was silently
    // ignored and draw() could read a stale/animated marker position.
    let lat, lng;
    const movePos = this.overlay_?.position;
    if (movePos) {
      lat = _.isFunction(movePos.lat) ? movePos.lat() : movePos.lat;
      lng = _.isFunction(movePos.lng) ? movePos.lng() : movePos.lng;
    } else {
      lat = this.marker_.position?.lat;
      lng = this.marker_.position?.lng;
      if (_.isFunction(this.marker_.position?.lat) && _.isFunction(this.marker_.position?.lng)) {
        lat = this.marker_.position?.lat();
        lng = this.marker_.position?.lng();
      }
    }

    if(!overlayProjection) {
      // Projection can be null during zoom/pan transitions.
      // Retry on the next animation frame so the overlay position
      // updates as soon as the projection becomes available.
      if (this.overlay_?.getMap()) {
        requestAnimationFrame(() => this.draw());
      }
      return;
    }
    // Use container-pixel coordinates because we attach the modal to the
    // map's outer div (sibling of the deck.gl canvas) instead of floatPane.
    // `fromLatLngToContainerPixel` returns coords relative to that outer
    // div, so the modal stays aligned without any parent transform.
    const latLng = new google.maps.LatLng(lat, lng); // eslint-disable-line no-undef
    const point = overlayProjection.fromLatLngToContainerPixel
      ? overlayProjection.fromLatLngToContainerPixel(latLng)
      : overlayProjection.fromLatLngToDivPixel(latLng);

    if (this.container_) {
      let x = point.x;
      let y = point.y;

      // `fromLatLngToContainerPixel` returns coords relative to the map's
      // outer div (`mapDiv`). When the modal is attached inside `.gm-style`
      // (or any other positioned ancestor that does not coincide pixel-for-
      // pixel with `mapDiv`), we must subtract the parent's offset relative
      // to `mapDiv` so absolute positioning lands on the right spot.
      // When attached directly to `mapDiv`, this resolves to (0, 0) and
      // behaves identically to the previous code.
      const map = this.overlay_?.getMap?.();
      const mapDiv = map?.getDiv?.();
      const parentEl = this._parentEl_;
      if (mapDiv && parentEl && parentEl !== mapDiv) {
        const mapRect = mapDiv.getBoundingClientRect();
        const parentRect = parentEl.getBoundingClientRect();
        x -= (parentRect.left - mapRect.left);
        y -= (parentRect.top - mapRect.top);
      }

      //const { rotation } = this.marker_.icon;
      y = y - 40;
      /* if (rotation >= 0 && rotation <= 90) {
        y = y - 8;
      } else if (rotation > 90 && rotation <= 180) {
        x = x - 20;
        y = y - 15;
      } else if (rotation > 180 && rotation <= 270) {
        y = y - 50;
      } else if (rotation > 270 && rotation < 360) {
        x = x + 20;
        y = y - 8;
      } */

      this.container_.style.left = `${x}px`;
      this.container_.style.top = `${y}px`;

      // Fire the callback only once (after first draw) to avoid triggering
      // React setState on every pan/zoom frame (~60 calls/sec).
      if (this.callback_ && !this.callbackFired_) {
        this.callbackFired_ = true;
        this.callback_(this);
      }
    }
  }
  /**
   * The onRemove() method will be called automatically from the API if
   * we ever set the overlay's map property to 'null'.
   */
  onRemove() {
    if (this.container_) {
      for (let i = 0; i < this.listeners.length; i++) {
        const { event, handler } = this.listeners[i];
        this.container_.removeEventListener(event, handler);
      }
      if (this.container_.parentNode) {
        this.container_.parentNode.removeChild(this.container_);
      }
      this.container_ = null;
    }
    this._parentEl_ = null;
    this.listeners = [];
  }

  onMove(position){

    if(this.overlay_ && position){
      this.overlay_.position = position;
      this.draw()
    }
  }

  setMap(map) {
    this.overlay_.setMap(map);
  }
}

export default CustomOverlay